import {useCallback, useEffect, useRef, useState} from 'react';
import {fetchEnrollment} from './enrollment';

// switching back to a tab fires both focus and visibilitychange, check only once
const CHECK_THROTTLE_MS = 5000;

const toTime = (value: string | null | undefined): number | null =>
    value ? new Date(value).getTime() : null;

type StaleEnrollmentCheck = {
    isStale: boolean;
    serverUpdatedAt: string | null;
    markStale: (serverUpdatedAt: string) => void;
    clearStale: () => void;
};

/**
 * Compares this tab's copy of the enrollment with the server whenever the tab regains focus,
 * so a tab left open while the enrollment changed elsewhere can stop before it saves over it.
 */
export const useStaleEnrollmentCheck = (
    enrollmentId: string | undefined,
    cachedUpdatedAt: string | undefined,
    isSaving: boolean,
): StaleEnrollmentCheck => {
    const [serverUpdatedAt, setServerUpdatedAt] = useState<string | null>(null);
    const lastCheckRef = useRef(0);
    const latestRef = useRef({cachedUpdatedAt, isSaving});

    useEffect(() => {
        latestRef.current = {cachedUpdatedAt, isSaving};
    });

    const check = useCallback(async () => {
        const now = Date.now();

        if (
            !enrollmentId ||
            latestRef.current.isSaving ||
            now - lastCheckRef.current < CHECK_THROTTLE_MS
        ) {
            return;
        }

        lastCheckRef.current = now;
        const cachedBefore = latestRef.current.cachedUpdatedAt;

        try {
            const serverEnrollment = await fetchEnrollment(enrollmentId);
            const {cachedUpdatedAt: cachedAfter, isSaving: savingAfter} = latestRef.current;

            // this tab saved while the check was in flight, the comparison is meaningless
            if (savingAfter || cachedAfter !== cachedBefore || !cachedAfter) {
                return;
            }

            if (toTime(serverEnrollment.updatedAt) !== toTime(cachedAfter)) {
                setServerUpdatedAt(serverEnrollment.updatedAt);
            }
        } catch (error) {
            // a failed check must never block the form
            console.error('stale enrollment check failed', error);
        }
    }, [enrollmentId]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void check();
            }
        };
        const handleFocus = () => {
            void check();
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [check]);

    const clearStale = useCallback(() => {
        setServerUpdatedAt(null);
    }, []);

    return {
        isStale: serverUpdatedAt !== null,
        serverUpdatedAt,
        markStale: setServerUpdatedAt,
        clearStale,
    };
};
